<template>
  <div class="setting-create-product">
    <el-form
      ref="formRef"
      :model="formData"
      label-position="top"
      label-width="120px"
      :rules="rules"
    >
      <!-- Información básica del elemento tipo -->
      <div class="basic-info-section">
        <product-basic-info-form
          ref="basicInfoFormRef"
          v-model="basicInfoData"
          :is-compact="false"
        />
      </div>

      <!-- Configuración de grupo -->
      <div class="group-config-section">
        <el-form-item>
          <div class="switch-container">
            <label class="switch-label">Crear grupo</label>
            <el-switch
              v-model="crearGrupo"
              size="default"
              :inactive-text="'No'"
              :active-text="'Sí'"
            />
          </div>
        </el-form-item>
      </div>

      <!-- Botones de acción -->
      <div class="action-buttons">
        <el-button @click="cancelar">Cancelar</el-button>
        <el-tooltip
          :content="
            isValid
              ? ''
              : 'Complete la descripción (mín. 3 caracteres) y seleccione un elemento base'
          "
          :disabled="isValid"
          placement="top"
        >
          <el-button
            type="primary"
            :loading="creando"
            :disabled="!isValid"
            @click="crear"
          >
            Crear Producto
          </el-button>
        </el-tooltip>
      </div>
    </el-form>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
} from 'vue';
import { ElMessage } from 'element-plus';
import ProductBasicInfoForm from './product_basic_info_form.vue';
import { useElementoTipoStore } from '@/stores/elemento_tipo/elemento_tipo_store.js';

const emit = defineEmits(['close', 'cancel', 'created']);

// Stores
const elementoTipoStore = useElementoTipoStore();

// Estados locales
const formRef = ref(null);
const basicInfoFormRef = ref(null);
const creando = ref(false);
const crearGrupo = ref(false);

// Datos del formulario básico
const basicInfoData = ref({
  Descripcion: '',
  ElementoBase: null,
  Activo: true,
});

// Datos del formulario (mantenido para compatibilidad con validación)
const formData = ref({
  Descripcion: '',
  Activo: true,
  CodigoElementoBase: null,
  ElementoBase: {},
});

// Reglas de validación
const rules = {
  Descripcion: [
    { required: true, message: 'La descripción es requerida', trigger: 'blur' },
    { min: 3, message: 'La descripción debe tener al menos 3 caracteres', trigger: 'blur' },
  ],
  CodigoElementoBase: [
    { required: true, message: 'El elemento base es requerido', trigger: 'change' },
  ],
};

// Watch para sincronizar basicInfoData con formData
watch(
  basicInfoData,
  (newValue) => {
    if (newValue) {
      formData.value.Descripcion = newValue.Descripcion;
      formData.value.Activo = newValue.Activo;
      formData.value.CodigoElementoBase = newValue.ElementoBase?.Codigo || null;
      formData.value.ElementoBase = newValue.ElementoBase || {};
    }
  },
  { deep: true },
);

// Computed para habilitar/deshabilitar el botón crear
const isValid = computed(() => {
  const tieneDescripcion =
    basicInfoData.value.Descripcion && basicInfoData.value.Descripcion.trim().length >= 3;
  const tieneElementoBase =
    basicInfoData.value.ElementoBase && basicInfoData.value.ElementoBase.Codigo;
  const formValido = basicInfoFormRef.value?.isValid || false;

  return tieneDescripcion && tieneElementoBase;
});

// Métodos
const crear = async () => {
  if (!isValid.value) return;

  try {
    creando.value = true;

    // Preparar datos para el store usando basicInfoData
    elementoTipoStore.elementoTipo = {
      Descripcion: basicInfoData.value.Descripcion,
      Activo: basicInfoData.value.Activo,
      CodigoElementoBase: basicInfoData.value.ElementoBase?.Codigo,
      ElementoBase: basicInfoData.value.ElementoBase || {},
      crearGrupo: crearGrupo.value,
    };

    // Crear el elemento tipo
    const nuevoElemento = await elementoTipoStore.createElementoTipoHTTP();

    if (nuevoElemento) {
      // Actualizar el listado en el store consolidado
      // await elementoTipoStore.getAllDetailedHTTP()

      ElMessage.success('Producto personalizado creado exitosamente');
      emit('created', {
        ...nuevoElemento,
        crearGrupo: crearGrupo.value,
      });
      emit('close');
    } else {
      throw new Error('No se pudo crear el elemento');
    }
  } catch (error) {
    console.error('Error al crear producto personalizado:', error);
    ElMessage.error('Error al crear el producto personalizado');
  } finally {
    creando.value = false;
  }
};

const cancelar = () => {
  // Limpiar formulario
  basicInfoData.value = {
    Descripcion: '',
    ElementoBase: null,
    Activo: true,
  };
  formData.value = {
    Descripcion: '',
    Activo: true,
    CodigoElementoBase: null,
    ElementoBase: {},
  };
  crearGrupo.value = false;
  emit('cancel');
};
</script>

<style scoped>
.setting-create-product {
  padding: 20px;
}

.basic-info-section {
  margin-bottom: 30px;
  padding: 15px 20px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background-color: #fafbfc;
  border-left: 4px solid #67c23a;
}

.group-config-section {
  margin-bottom: 30px;
  padding: 15px 20px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background-color: #f8f9fa;
  border-left: 4px solid #409eff;
}

.switch-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.switch-label {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin: 0;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}
</style>
